import React from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import { cn } from '../../utils';

const LoaderVariants = cva('', {
  variants: {
    animation: {
      spin: 'animate-ring-spin',
      progressPulse: 'animate-ring-progress'
    }
  },
  defaultVariants: {
    animation: 'spin'
  }
});

type LoaderVariantProps = VariantProps<typeof LoaderVariants>;

export const LOADER_SIZES = {
  sm: { size: 20, strokeWidth: 3 },
  md: { size: 48, strokeWidth: 4 },
  lg: { size: 72, strokeWidth: 6 }
} as const;

export type LoaderSize = keyof typeof LOADER_SIZES;

export interface LoaderProps extends LoaderVariantProps {
  /** Predefined size variant. @default 'md' */
  size?: LoaderSize;
  arcLength?: number;
  speed?: number;
}

const LoaderBase: React.FC<LoaderProps> = ({
  size = 'md',
  arcLength = 0.25,
  animation = 'spin',
  speed = animation === 'spin' ? 1400 : 2500
}) => {
  const preset = LOADER_SIZES[size];
  const resolvedSize = preset.size;
  const resolvedStrokeWidth = preset.strokeWidth;
  const radius = (resolvedSize - resolvedStrokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashLength = arcLength * circumference;
  const color = 'rgb(var(--secondary-base))';
  const trackColor = 'rgb(var(--border-base))';

  return (
    <svg
      role='img'
      aria-label='Loading'
      width={resolvedSize}
      height={resolvedSize}
      viewBox={`0 0 ${resolvedSize} ${resolvedSize}`}
      xmlns='http://www.w3.org/2000/svg'
    >
      <circle
        cx={resolvedSize / 2}
        cy={resolvedSize / 2}
        r={radius}
        stroke={trackColor}
        strokeWidth={resolvedStrokeWidth}
        fill='none'
      />
      <circle
        className={cn(LoaderVariants({ animation }))}
        cx={resolvedSize / 2}
        cy={resolvedSize / 2}
        r={radius}
        stroke={color}
        strokeWidth={resolvedStrokeWidth}
        fill='none'
        strokeDasharray={`${dashLength} ${circumference}`}
        strokeDashoffset='0'
        strokeLinecap='round'
        style={{ transformOrigin: '50% 50%', animationDuration: `${speed}ms` }}
      />
      <style>{`
        @keyframes ring-spin {
          100% {
            transform: rotate(360deg);
          }
        }


        @keyframes ring-progress {
          0% {
            stroke-dasharray: 0 ${circumference};
            stroke-dashoffset: 0;
          }
          33% {
            stroke-dasharray: ${circumference} ${circumference};
            stroke-dashoffset: 0;
          }
          66% {
            stroke-dasharray: ${circumference} ${circumference};
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dasharray: 0 ${circumference};
            stroke-dashoffset: -${circumference};
          }
        }

        .animate-ring-spin {
          animation: ring-spin ${speed}ms linear infinite;
        }

        .animate-ring-progress {
           transform: rotate(270deg);
          animation: ring-progress ${speed}ms ease-in-out infinite;
        }
      `}</style>
    </svg>
  );
};

/** Full-page centered loader — for route loading states and auth guards. */
const LoaderPage: React.FC<Pick<LoaderProps, 'size' | 'animation'>> = props => (
  <div className='flex items-center justify-center min-h-screen animate-fade-in'>
    <Loader {...props} />
  </div>
);

LoaderPage.displayName = 'Loader.Page';

/** Section centered loader — for panel / card loading states. */
const LoaderSection: React.FC<Pick<LoaderProps, 'size' | 'animation'> & { className?: string }> = ({
  className,
  ...props
}) => (
  <div className={cn('flex items-center justify-center py-lg', className)}>
    <Loader {...props} />
  </div>
);

LoaderSection.displayName = 'Loader.Section';

const Loader = Object.assign(LoaderBase, {
  Page: LoaderPage,
  Section: LoaderSection
});

export { Loader };
